/**
 * RosterRow — one booking in the Bookings tab (BON-199, extracted BON-490).
 * Each row LINKS to its booking detail page at `/admin/bookings/:ref`. Non-active
 * rows (rebooked / refunded / cancelled) are shown but de-emphasized and never
 * counted.
 */
import { Link } from 'react-router-dom';

import type { RosterBooking } from '../../../../api/workshops';

/** `BKG-` + the first 6 chars of the booking id, uppercased. */
export function bookingRef(bookingId: string): string {
  return `BKG-${bookingId.replace(/-/g, '').slice(0, 6).toUpperCase()}`;
}

/** A booking is "active" (sold — counts toward the headline) iff CONFIRMED. */
function isActiveStatus(status: string): boolean {
  return status === 'CONFIRMED';
}

/** Map the ops status (+ comp flag) to a display label + colors. Comps win over
 *  the plain CONFIRMED label. REFUNDED and CANCELLED both render as "Cancelled"
 *  (REFUNDED = native refund; CANCELLED = a FareHarbor-migrated cancellation —
 *  both terminal, seat released). */
function badgeFor(booking: RosterBooking): { label: string; color: string; bg: string } {
  if (booking.isComp) {
    return {
      label: 'Comp',
      color: 'var(--nb-warn)',
      bg: 'var(--nb-warn-soft, rgba(180,120,0,0.12))',
    };
  }
  switch (booking.status) {
    case 'CONFIRMED':
      return {
        label: 'Confirmed',
        color: 'var(--nb-primary)',
        bg: 'var(--nb-primary-soft, rgba(40,140,90,0.12))',
      };
    // SUPERSEDED is the deploy-window alias for REBOOKED.
    case 'SUPERSEDED':
    case 'REBOOKED':
      return { label: 'Rebooked', color: 'var(--nb-subtle)', bg: 'rgba(120,120,120,0.12)' };
    case 'REFUNDED':
    case 'CANCELLED':
      return { label: 'Cancelled', color: 'var(--nb-danger, #c0392b)', bg: 'rgba(192,57,43,0.10)' };
    default:
      return { label: booking.status, color: 'var(--nb-subtle)', bg: 'rgba(120,120,120,0.12)' };
  }
}

export function RosterRow({ booking }: { booking: RosterBooking }) {
  // Non-sold bookings (rebooked / refunded / cancelled) are shown but visually
  // de-emphasized so "what counts toward the headline" reads at a glance.
  const muted = !isActiveStatus(booking.status);
  // Display the human `BKG-` ref, but navigate by the real ops `Booking.id`
  // UUID — the booking-detail route (BON-145) resolves the ops id, not the
  // derived display code (which previously dead-ended).
  const ref = bookingRef(booking.bookingId);
  const { label, color, bg } = badgeFor(booking);
  return (
    <Link
      to={`/admin/bookings/${booking.bookingId}`}
      data-testid="roster-row"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '8px 0',
        borderTop: '1px solid var(--nb-border-2)',
        opacity: muted ? 0.6 : 1,
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: 'var(--nb-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {booking.customerName}
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--nb-subtle)' }}>
          {ref}
          {booking.isComp ? ' · Manual · CSR' : ''}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <span style={{ fontSize: 11.5, color: 'var(--nb-subtle)', whiteSpace: 'nowrap' }}>
          {booking.ticketsCount} tickets
        </span>
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 999,
            whiteSpace: 'nowrap',
            color,
            background: bg,
          }}
        >
          {label}
        </span>
        <span aria-hidden="true" style={{ color: 'var(--nb-subtle)', fontSize: 15, lineHeight: 1 }}>
          ›
        </span>
      </div>
    </Link>
  );
}
